import { AuthOptions } from './AuthOptions';
import { motion } from 'framer-motion';
import { Sparkles, Shield, Zap } from 'lucide-react';

export default function LoginPage() {
  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      {/* Large decorative gradient orbs */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-blue-200/40 to-indigo-300/30 rounded-full blur-3xl opacity-50 animate-pulse" />
      <div className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-tr from-purple-200/40 to-pink-300/30 rounded-full blur-3xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-cyan-200/20 to-blue-300/20 rounded-full blur-3xl opacity-30" />
      
      {/* Small floating particles */}
      <div className="absolute top-32 left-1/4 w-2 h-2 bg-blue-400 rounded-full animate-bounce opacity-60" style={{ animationDelay: '0.5s' }} />
      <div className="absolute bottom-32 right-1/4 w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce opacity-60" style={{ animationDelay: '1.5s' }} />
      <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-purple-400 rounded-full animate-bounce opacity-60" style={{ animationDelay: '2s' }} />

      <div className="relative flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4 py-12">
        {/* Logo/Brand section with animation */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-8 text-center"
        >
          <div className="relative inline-block">
            {/* Logo container with glow effect */}
            <div className="p-4 bg-gradient-to-br from-white via-blue-50 to-indigo-50 rounded-3xl shadow-2xl border border-blue-100/50 backdrop-blur-sm relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative">
                <Sparkles className="h-8 w-8 text-blue-600 mx-auto mb-2 group-hover:scale-110 transition-transform duration-300" />
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">
                  Rainfactory
                </h1>
              </div>
            </div>
            
            {/* Floating decorative elements around logo */}
            <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-bounce opacity-80" style={{ animationDelay: '0.5s' }} />
            <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full animate-bounce opacity-80" style={{ animationDelay: '1s' }} />
          </div>
          
          {/* Welcome message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-6"
          >
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Welcome back</h2>
            <p className="text-gray-600 text-sm max-w-md mx-auto leading-relaxed">
              Sign in to access your AI-powered marketing suite and continue growing your business.
            </p>
          </motion.div>
        </motion.div>

        {/* Main glassmorphic card */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
          className="group relative w-full max-w-md"
        >
          {/* Card glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-purple-500/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform scale-105" />
          
          {/* Main card */}
          <div className="relative overflow-hidden bg-white/70 backdrop-blur-md rounded-3xl border border-white/50 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-[1.02]">
            {/* Gradient border effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-blue-50/40 to-indigo-50/60 rounded-3xl" />
            <div className="absolute inset-[1px] bg-white/60 backdrop-blur-md rounded-3xl" />
            
            {/* Decorative gradient overlay */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full -translate-y-16 translate-x-16" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-100/40 to-transparent rounded-full translate-y-12 -translate-x-12" />
            
            {/* Inner grid pattern */}
            <div className="absolute inset-0 bg-grid-pattern opacity-3" />
            
            {/* Content */}
            <div className="relative p-8">
              {/* Floating security indicators */}
              <div className="absolute top-4 right-4 flex gap-2">
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="p-1.5 bg-gradient-to-br from-green-100 to-emerald-200 rounded-lg shadow-sm"
                >
                  <Shield className="h-3 w-3 text-green-600" />
                </motion.div>
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  className="p-1.5 bg-gradient-to-br from-blue-100 to-cyan-200 rounded-lg shadow-sm"
                >
                  <Zap className="h-3 w-3 text-blue-600" />
                </motion.div>
              </div>
              
              <AuthOptions 
                mode="login" 
                showLoginLink={true}
                showTermsFooter={true}
              />
            </div>
          </div>
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-8 flex items-center justify-center gap-6 text-xs text-gray-500"
        >
          <div className="flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-green-500" />
            <span>Secure & Encrypted</span>
          </div>
          <div className="w-1 h-1 bg-gray-300 rounded-full" />
          <div className="flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-blue-500" />
            <span>Lightning Fast</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
} 